import { ReactNode, useMemo, useState } from 'react';

import { Box, Collapse, List, ListItemButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import Card from '~components/containers/card';
import { pagePathSegments } from '~constants';
import {
	filterAuthorizedWikiNodes,
	findWikiPageByFile,
	isWikiPageAuthorized,
} from '~features/wiki/utils/wikiManifestAccess';
import { useSession } from '~hooks';
import Dimens from '~themes/dimens';

import wikiManifest from '../../../docs/manifest.json';
import {
	buildTocTree,
	maxDepth,
	nodeContainsPageId,
	TocNode,
	type WikiManifest,
} from '../utils/wikiTocTree';

const WikiToc = () => {
	const { t } = useTranslation('nav');
	const { id: activeWikiId } = useParams();
	const { user } = useSession();

	type ExpandedState = Record<string, boolean>;
	// Default: everything collapsed.
	// Sections that contain the active page will still auto-expand via `nodeContainsPageId(...)`.
	const [expanded, setExpanded] = useState<ExpandedState>({});

	const wikiRoot = useMemo(() => `/${pagePathSegments.Wiki}` as const, []);

	const itemHorizontalPadding = Dimens.spacingXl;

	const tocTree = useMemo(() => {
		const manifest = wikiManifest as unknown as WikiManifest;
		const filteredToc = filterAuthorizedWikiNodes(manifest.toc, user?.role);
		return buildTocTree(filteredToc);
	}, [user?.role]);

	const canAccessIndex = useMemo(() => {
		const manifest = wikiManifest as unknown as WikiManifest;
		const indexPath = manifest.indexPath;
		if (!indexPath) {
			return false;
		}

		const tocIndexPage = findWikiPageByFile(manifest.toc, indexPath);
		if (!tocIndexPage) {
			return true;
		}

		return isWikiPageAuthorized(tocIndexPage, user?.role);
	}, [user?.role]);

	const renderNode = (node: TocNode, depth: number): ReactNode => {
		const indent = `calc(${itemHorizontalPadding} * ${Math.min(depth, maxDepth)})`;

		if (node.type === 'section') {
			const isOpen = expanded[node.key] ?? nodeContainsPageId(node, activeWikiId);
			return (
				<Box key={node.key}>
					<ListItemButton
						onClick={() => setExpanded((prev) => ({ ...prev, [node.key]: !isOpen }))}
						sx={{
							mx: Dimens.spacingNone,
							px: itemHorizontalPadding,
							py: Dimens.spacingLg,
							borderRadius: Dimens.radiusMd,
							pl: `calc(${indent} + ${itemHorizontalPadding})`,
							'&:hover .toc-section-text': { textDecoration: 'underline' },
						}}
					>
						<Typography
							className='toc-section-text'
							variant='labelSm'
							color='text.primary'
							sx={{
								textTransform: 'uppercase',
								userSelect: 'none',
							}}
						>
							{node.title}
						</Typography>
						<Typography
							variant='labelSm'
							color='text.primary'
							sx={{ marginLeft: 'auto', userSelect: 'none' }}
						>
							{isOpen ? '−' : '+'}
						</Typography>
					</ListItemButton>
					<Collapse in={isOpen} timeout='auto' unmountOnExit>
						{node.children.map((child) => renderNode(child, depth + 1))}
					</Collapse>
				</Box>
			);
		}

		const isActive = node.id === activeWikiId;
		const to = `${wikiRoot}/${node.id}`;

		return (
			<ListItemButton
				key={node.key}
				component={Link}
				to={to}
				sx={{
					mx: Dimens.spacingNone,
					px: itemHorizontalPadding,
					py: Dimens.spacingMd,
					pl: `calc(${indent} + ${itemHorizontalPadding})`,
					borderRadius: Dimens.radiusMd,
					'&:hover .toc-link-text': { textDecoration: 'underline' },
				}}
			>
				<Typography
					className='toc-link-text'
					variant={'paragraphSm'}
					color={isActive ? 'text.primary' : 'text.secondary'}
					sx={{ textDecoration: 'none' }}
				>
					{node.title}
				</Typography>
			</ListItemButton>
		);
	};

	return (
		<Card>
			<Typography variant='headingXs' color='text.secondary'>
				{t('tableOfContents')}
			</Typography>
			<List dense disablePadding title={t('tableOfContents')} sx={{ pt: Dimens.spacingXl }}>
				{canAccessIndex && (
					<ListItemButton
						component={Link}
						to={wikiRoot}
						sx={{
							mx: Dimens.spacingNone,
							px: itemHorizontalPadding,
							py: Dimens.spacingMd,
							borderRadius: Dimens.radiusMd,
							'&:hover .toc-link-text': { textDecoration: 'underline' },
						}}
					>
						<Typography
							className='toc-link-text'
							variant={'paragraphSm'}
							color={!activeWikiId ? 'text.primary' : 'text.secondary'}
							sx={{ textDecoration: 'none' }}
						>
							{t('index')}
						</Typography>
					</ListItemButton>
				)}

				{tocTree.map((node) => renderNode(node, 0))}
			</List>
		</Card>
	);
};

export default WikiToc;
